import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';

export interface UseAdminCsvDownloadMutationOptions<TVariables = void> {
  /** Message shown on successful CSV download. */
  successMessage?: string;
  /** Fallback message when API error has no message. */
  errorMessage?: string;
  /** Optional extra useMutation options. */
  mutationOptions?: Omit<
    UseMutationOptions<void, Error, TVariables>,
    'mutationFn' | 'onSuccess' | 'onError'
  >;
}

const DEFAULT_SUCCESS_MESSAGE = 'CSVを出力しました。';
const DEFAULT_ERROR_MESSAGE = 'CSVの出力に失敗しました。';

/**
 * Shared mutation hook for admin CSV download.
 * Centralizes toast onSuccess/onError per project rule (useAppToast in mutation hook).
 *
 * @param mutationFn - Async function that performs the CSV request and triggers file download.
 * @param options - Optional success/error messages and useMutation options.
 * @returns useMutation result (mutateAsync, isPending, etc.).
 */
export function useAdminCsvDownloadMutation<TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<void>,
  options: UseAdminCsvDownloadMutationOptions<TVariables> = {}
) {
  const toast = useAppToast();
  const {
    successMessage = DEFAULT_SUCCESS_MESSAGE,
    errorMessage = DEFAULT_ERROR_MESSAGE,
    mutationOptions,
  } = options;

  return useMutation<void, Error, TVariables>({
    ...mutationOptions,
    mutationFn,
    onSuccess: () => {
      toast.success(successMessage);
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, errorMessage));
    },
  });
}
